import { Box, Snackbar, Alert } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";

interface CasusFormData {
  MICRDS_ID: string;
  SCORE: number;
  NEUROLOGICAL_COUNT: string;
  NEUROLOGICAL_COUNT_XVALUE: string;
  PLATELET_COUNT: string;
  PLATELET_COUNT_XVALUE: string;
  VENTRI_ASSIST_DEVICE: string;
  VENTRI_ASSIST_DEVICE_XVALUE: string;
  INTRA_BALL_PUMP: string;
  INTRA_BALL_PUMP_XVALUE: string;
  LACTIC_ACID: string;
  LACTIC_ACID_XVALUE: string;
  PAR: string;
  PAR_XVALUE: string;
  BILIRUBIN: string;
  BILIRUBIN_XVALUE: string;
  CVVH: string;
  CVVH_XVALUE: string;
  CREATININE: string;
  CREATININE_XVALUE: string;
  RESPIRATORY: string;
  RESPIRATORY_XVALUE: string;
  CUSER: string;
  NURSE_ID: string;
  PROCESS_TIME: Date;
  CTIME: Date;
  MCS_ID: string;
}

interface CasusScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof CasusFormData, value: any) => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function CasusScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  isSaving,
  extraAccess,
}: CasusScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "CASUS_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof CasusFormData, value);
      });
    },
  });

  const typedFormData = formData as CasusFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof CasusFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = () => {
    if (!typedFormData) return;

    const scoreFields = [
      "RESPIRATORY_XVALUE",
      "CREATININE_XVALUE",
      "CVVH_XVALUE",
      "BILIRUBIN_XVALUE",
      "PAR_XVALUE",
      "LACTIC_ACID_XVALUE",
      "INTRA_BALL_PUMP_XVALUE",
      "VENTRI_ASSIST_DEVICE_XVALUE",
      "PLATELET_COUNT_XVALUE",
      "NEUROLOGICAL_COUNT_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("SCORE", score);
  };

  useEffect(() => {
    handleScoreCalculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData?.RESPIRATORY_XVALUE,
    typedFormData?.CREATININE_XVALUE,
    typedFormData?.CVVH_XVALUE,
    typedFormData?.BILIRUBIN_XVALUE,
    typedFormData?.PAR_XVALUE,
    typedFormData?.LACTIC_ACID_XVALUE,
    typedFormData?.INTRA_BALL_PUMP_XVALUE,
    typedFormData?.VENTRI_ASSIST_DEVICE_XVALUE,
    typedFormData?.PLATELET_COUNT_XVALUE,
    typedFormData?.NEUROLOGICAL_COUNT_XVALUE,
  ]);


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.MCS_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false}></ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.respiratory")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.respiratory")}
                  mcpId={cardData.mcpConfig.RESPIRATORY.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("RESPIRATORY", selected);
                    }
                  }}
                  value={selectedMCPs.RESPIRATORY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.RESPIRATORY.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.renal")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.creatinin")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <Grid xs={12} sm={6} md={6}>
                  <ByzMcParameterDetailSelect
                    title={t("casus.creatinin")}
                    mcpId={cardData.mcpConfig.CREATININE.mcpId}
                    multiple={false}
                    handleChange={(val) => {
                      if (val && !Array.isArray(val)) {
                        const selected = val as McParameterDetailResponseDto;
                        handleMCPChange("CREATININE", selected);
                      }
                    }}
                    value={selectedMCPs.CREATININE}
                    sx={{ width: "100%" }}
                    cachedData={getMCPParameterDetails(
                      cardData.mcpConfig.CREATININE.mcpId
                    )}
                    cacheLoaded={mcpCacheLoaded}
                    readOnly={!extraAccess}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.cvhh")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.cvhh")}
                  mcpId={cardData.mcpConfig.CVVH.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("CVVH", selected);
                    }
                  }}
                  value={selectedMCPs.CVVH}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.CVVH.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.liver")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.bilirrubin")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.bilirrubin")}
                  mcpId={cardData.mcpConfig.BILIRUBIN.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("BILIRUBIN", selected);
                    }
                  }}
                  value={selectedMCPs.BILIRUBIN}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.BILIRUBIN.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.cardiovascular")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.par")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.par")}
                  mcpId={cardData.mcpConfig.PAR.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("PAR", selected);
                    }
                  }}
                  value={selectedMCPs.PAR}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.PAR.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.lactic")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.lactic")}
                  mcpId={cardData.mcpConfig.LACTIC_ACID.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LACTIC_ACID", selected);
                    }
                  }}
                  value={selectedMCPs.LACTIC_ACID}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.LACTIC_ACID.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.intra")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.intra")}
                  mcpId={cardData.mcpConfig.INTRA_BALL_PUMP.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("INTRA_BALL_PUMP", selected);
                    }
                  }}
                  value={selectedMCPs.INTRA_BALL_PUMP}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.INTRA_BALL_PUMP.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.ventri")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.ventri")}
                  mcpId={cardData.mcpConfig.VENTRI_ASSIST_DEVICE.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("VENTRI_ASSIST_DEVICE", selected);
                    }
                  }}
                  value={selectedMCPs.VENTRI_ASSIST_DEVICE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.VENTRI_ASSIST_DEVICE.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.koagulaion")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.trombocit")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.trombocit")}
                  mcpId={cardData.mcpConfig.PLATELET_COUNT.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("PLATELET_COUNT", selected);
                    }
                  }}
                  value={selectedMCPs.PLATELET_COUNT}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.PLATELET_COUNT.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.santral-system")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.neurology-state")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("casus.neurology-state")}
                  mcpId={cardData.mcpConfig.NEUROLOGICAL_COUNT.mcpId}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("NEUROLOGICAL_COUNT", selected);
                    }
                  }}
                  value={selectedMCPs.NEUROLOGICAL_COUNT}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig.NEUROLOGICAL_COUNT.mcpId
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={false} fontWeight={1000}>
                {t("casus.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }} isTitle={false}>
                  {t("casus.total-score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default CasusScoreCreate;
